import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { useCartStore } from "../../store/cartStore";
import { CURRENCY_MAP, serviceKeys } from "../../constants";
import countryList from "react-select-country-list";
import Select from "react-select";
import { createPublicOrder } from "../../api/ticketsPlatform/checkout";
import { useUserStore } from "../../store/userStore";
import Navbar from "../../components/tickets/navBar";
import Footer from "../../components/tickets/footer";
import { usePixel } from "../../hooks/usePixel";
import { allCountries } from "country-telephone-data";

const getRemaining = (ticket) => {
  if (ticket.quantity_total != null && ticket.quantity_sold != null) {
    return Math.max(0, ticket.quantity_total - ticket.quantity_sold);
  }
  return 20;
};

const countries = countryList().getData();

const getFlagEmoji = (countryCode) =>
  countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));

const phoneCountryOptions = allCountries.map((country) => ({
  label: `${getFlagEmoji(country.iso2)} +${country.dialCode}`,
  value: `+${country.dialCode}`,
}));

const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#031c14",
    borderColor: state.isFocused ? "rgba(34,197,94,0.6)" : "rgba(20,83,45,0.4)",
    borderRadius: "0.75rem",
    padding: "2px 4px",
    boxShadow: "none",
    "&:hover": { borderColor: "rgba(34,197,94,0.4)" },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#031c14",
    border: "1px solid rgba(20,83,45,0.4)",
    borderRadius: "0.75rem",
    overflow: "hidden",
  }),
  menuList: (base) => ({ ...base, padding: 0 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#042018" : "transparent",
    color: "#fff",
    fontSize: "0.875rem",
    cursor: "pointer",
  }),
  singleValue: (base) => ({ ...base, color: "#fff", fontSize: "0.875rem" }),
  input: (base) => ({ ...base, color: "#fff", fontSize: "0.875rem" }),
  placeholder: (base) => ({ ...base, color: "#4b5563", fontSize: "0.875rem" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "rgba(34,197,94,0.4)" }),
};

export default function Checkout() {
  const navigate = useNavigate();
  const {
    items,
    removeItem,
    updateQuantity,
    getSubtotal,
    getCommission,
    getVat,
    getTotal,
    tables: cartTables,
    removeTable,
  } = useCartStore();

  const subtotal = getSubtotal();
  const commission = getCommission();
  const total = getTotal();
  const vat = getVat();
  const hasCommission = commission > 0;
  const hasVat = vat > 0;
  const currency = items[0]?.currency || cartTables[0]?.currency;

  const { trackEvent } = usePixel(items[0]?.pixels || []);

  const { isAuthenticated, token, user } = useUserStore();

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    gender: "",
    address: "",
    city: "",
    postcode: "",
    country: null,
    phoneCode: "+34",
    phone: "",
    voucherCode: "",
    affiliateCode: "",
    createAccount: true,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      setForm((f) => ({
        ...f,
        fullName: `${user.first_name} ${user.last_name}` || "",
        email: user.email || "",
        gender: user.gender || "",
        phoneCode: user.phone_country || "+34",
        phone: user.phone_number || "",
        address: user.billing_address?.address || "",
        city: user.billing_address?.city || "",
        postcode: user.billing_address?.postcode || "",
        country: user.billing_address
          ? {
              label: user.billing_address.country,
              value: user.billing_address.country_code,
            }
          : null,
      }));
    }
  }, [isAuthenticated, user]);

  const formatCurrency = (amount, cur) => {
    const symbol = CURRENCY_MAP[cur || currency] || cur || currency;
    return `${symbol} ${parseFloat(amount).toFixed(2)}`;
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Requerido";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
      e.email = "Email inválido";
    if (!form.address.trim()) e.address = "Requerido";
    if (!form.city.trim()) e.city = "Requerido";
    if (!form.postcode.trim()) e.postcode = "Requerido";
    if (!form.country) e.country = "Requerido";
    return e;
  };

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
    if (apiError) setApiError(null);
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }

    const payload = {
      event: items[0]?.eventId || cartTables[0]?.eventId,
      email: form.email,
      full_name: form.fullName,
      gender: form.gender,
      phone_country: form.phoneCode,
      phone_number: form.phone,
      billing_address: {
        address: form.address,
        city: form.city,
        postcode: form.postcode,
        country: form.country.label,
        country_code: form.country.value,
      },
      items: items.map((i) => ({
        ticket_type_id: i.ticket.id,
        quantity: i.quantity,
      })),
      create_account: form.createAccount,
      ...(cartTables.length > 0 && {
        tables: cartTables.map((t) => ({
          table_id: t.id,
          // custom_fields_values: {
          //   address: form.address, // ⚠️ confirm key with backend
          //   phone: `${form.phoneCode}${form.phone}`, // ⚠️ confirm key with backend
          // },
        })),
      }),
      // ...(form.voucherCode.trim() && { voucher_code: form.voucherCode.trim() }),
      // ...(form.affiliateCode.trim() && { affiliate_code: form.affiliateCode.trim() }),
    };

    try {
      setSubmitting(true);
      setApiError(null);
      const order = await createPublicOrder(
        payload,
        isAuthenticated ? token : null,
      );

      trackEvent("SubmitCheckoutForm", {
        value: total,
        currency: currency,
      });

      navigate("/tickets-store/payment", {
        state: {
          orderNumber: order.order_number,
          total: order.total,
          currency: order.currency,
          expiresAt:
            order.expires_in_minutes != null
              ? Date.now() + order.expires_in_minutes * 60 * 1000
              : Date.now() + 5 * 60 * 1000,
          clientSecret: order.client_secret,
          stripePublicKey: order.stripe_public_key,
          buyerEmail: form.email,
          pixels: items[0]?.pixels || [],
        },
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Algo salió mal. Inténtalo de nuevo.";
      setApiError(message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── EMPTY STATE ── */
  if (items.length === 0 && (cartTables || []).length === 0) {
    return (
      <div className="bg-[#021610] text-white min-h-screen flex flex-col items-center justify-center gap-4">
        <span className="text-5xl">🎟</span>
        <p className="text-lg font-semibold">Tu carrito está vacío</p>
        <button
          onClick={() => navigate("/tickets-store/")}
          className="mt-2 px-6 py-2 rounded-xl border border-green-500 text-green-400 text-sm hover:bg-green-500 hover:text-black transition"
        >
          Ver eventos
        </button>
      </div>
    );
  }

  return (
    <div className="bg-[#021610] text-white min-h-screen">
      {/* NAVBAR */}
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 pt-24 pb-16">
        {/* BACK */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-green-400 hover:text-green-300 transition mb-8"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Volver
        </button>

        {/* STEPPER */}
        <div className="flex items-center w-full text-xs text-green-400/70 mb-10">
          <StepDot number="1" label="TIPO DE ENTRADA" done />
          <StepLine />
          <StepDot number="2" label="CONFIRMAR" active />
          <StepLine />
          <StepDot number="3" label="PAGO" />
        </div>

        <p className="text-[10px] tracking-widest text-green-400/70 uppercase">
          PASO 2
        </p>
        <h1 className="text-3xl md:text-4xl font-bold mt-1 mb-10">
          Confirma tu pedido
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* ── LEFT — FORM ── */}
          <div className="lg:col-span-3 space-y-6">
            <div className="space-y-4">
              <SectionLabel label="Información de contacto" />
              <Field
                label="Nombre completo"
                value={form.fullName}
                onChange={(v) => handleChange("fullName", v)}
                error={errors.fullName}
                placeholder="Tu nombre y apellidos"
              />
              <Field
                label="Email"
                type="email"
                value={form.email}
                disabled={isAuthenticated}
                onChange={(v) => handleChange("email", v)}
                error={errors.email}
                placeholder="tu@email.com"
              />

              {/* GENDER */}
              <div className="flex flex-col gap-1">
                <label className="text-[10px] tracking-widest text-gray-400 uppercase">
                  Género
                </label>
                <div className="relative">
                  <select
                    value={form.gender}
                    onChange={(e) => handleChange("gender", e.target.value)}
                    className="w-full bg-[#031c14] border border-green-900/40 rounded-xl px-4 py-3 text-sm text-white outline-none transition focus:border-green-500/60 appearance-none cursor-pointer pr-10"
                  >
                    <option value="" disabled>
                      Selecciona tu género
                    </option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {/* CHEVRON ICON */}
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-green-400/50">
                    <ChevronDownIcon className="w-4 h-4" />
                  </div>
                </div>
              </div>

              {/* PHONE */}
              <div className="flex flex-col gap-1">
                <label className="text-[10px] tracking-widest text-gray-400 uppercase">
                  Teléfono
                </label>
                <div className="flex items-center gap-2">
                  {/* COUNTRY CODE */}
                  <div className="w-32 flex-shrink-0">
                    <Select
                      options={phoneCountryOptions}
                      value={phoneCountryOptions.find(
                        (o) => o.value === form.phoneCode,
                      )}
                      onChange={(v) => handleChange("phoneCode", v.value)}
                      styles={{
                        ...selectStyles,
                        control: (base, state) => ({
                          ...selectStyles.control(base, state),
                          padding: "1px 2px",
                        }),
                      }}
                      menuPlacement="auto"
                    />
                  </div>
                  {/* NUMBER */}
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={form.phone}
                    onChange={(e) =>
                      handleChange("phone", e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="600 000 000"
                    className="flex-1 bg-[#031c14] border border-green-900/40 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition focus:border-green-500/60"
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500 leading-relaxed">
                Las entradas se enviarán a este email. Asegúrate de que es
                correcto.
              </p>

              {/* CREATE ACCOUNT CHECKBOX */}
              {!isAuthenticated && (
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={form.createAccount}
                      onChange={(e) =>
                        handleChange("createAccount", e.target.checked)
                      }
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded-md border transition flex items-center justify-center
        ${
          form.createAccount
            ? "bg-green-500 border-green-500"
            : "bg-[#031c14] border-green-900/40 group-hover:border-green-500/50"
        }`}
                    >
                      {form.createAccount && (
                        <span className="text-black text-xs font-bold leading-none">
                          ✓
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-white">Crear una cuenta</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Te enviaremos un email para activar tu cuenta y gestionar
                      tus entradas.
                    </p>
                  </div>
                </label>
              )}
            </div>

            <div className="space-y-4">
              <SectionLabel label="Dirección de facturación" />
              <Field
                label="Dirección"
                value={form.address}
                onChange={(v) => handleChange("address", v)}
                error={errors.address}
                placeholder="Calle, número, piso..."
              />
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Ciudad"
                  value={form.city}
                  onChange={(v) => handleChange("city", v)}
                  error={errors.city}
                  placeholder="Madrid"
                />
                <Field
                  label="Código postal"
                  value={form.postcode}
                  onChange={(v) => handleChange("postcode", v)}
                  error={errors.postcode}
                  placeholder="28001"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] tracking-widest text-gray-400 uppercase">
                  País
                </label>
                <Select
                  options={countries}
                  value={form.country}
                  onChange={(v) => handleChange("country", v)}
                  placeholder="Selecciona un país"
                  styles={selectStyles}
                  menuPlacement="auto"
                />
                {errors.country && (
                  <p className="text-[10px] text-red-400 tracking-wide">
                    {errors.country}
                  </p>
                )}
              </div>
            </div>

            {/* <div className="space-y-4">
              <SectionLabel label="Códigos opcionales" />
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Código de descuento"
                  value={form.voucherCode}
                  onChange={(v) => handleChange("voucherCode", v)}
                  placeholder="DESCUENTO10"
                />
                <Field
                  label="Código de afiliado"
                  value={form.affiliateCode}
                  onChange={(v) => handleChange("affiliateCode", v)}
                  placeholder="AFFY123"
                />
              </div>
            </div> */}

            {apiError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
                <p className="text-sm text-red-400">{apiError}</p>
              </div>
            )}
          </div>

          {/* ── RIGHT — ORDER SUMMARY ── */}
          <div className="lg:col-span-2">
            <SectionLabel label="Resumen del pedido" />

            <div className="space-y-3 mt-4">
              {items.map((item) => (
                <OrderItem
                  key={`${item.eventId}-${item.ticket.id}`}
                  item={item}
                  onRemove={() => removeItem(item.ticket.id, item.eventId)}
                  onQtyChange={(q) =>
                    updateQuantity(item.ticket.id, item.eventId, q)
                  }
                  formatCurrency={formatCurrency}
                />
              ))}
            </div>

            {/* TABLE ITEMS */}
            {cartTables.map((table) => (
              <div
                key={`table-${table.id}`}
                className="bg-[#031c14] border border-green-900/30 rounded-xl p-4 mt-2"
              >
                <p className="text-[10px] tracking-widest text-green-400/60 uppercase mb-1">
                  Mesa
                </p>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold">Mesa {table.name}</p>
                  <button
                    onClick={() => removeTable(table.id)}
                    className="text-gray-600 hover:text-red-400 transition flex-shrink-0 mt-0.5"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-xs text-gray-500">1 mesa</p>
                  <p className="text-sm font-semibold text-white">
                    {formatCurrency(
                      parseFloat(table.display_price || 0),
                      currency,
                    )}
                  </p>
                </div>
              </div>
            ))}

            {/* PRICE BREAKDOWN */}
            <div className="mt-5 space-y-2">
              {/* SUBTOTAL */}
              <div className="flex items-center justify-between">
                <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                  Subtotal
                </p>
                <p className="text-sm text-white">
                  {formatCurrency(subtotal, currency)}
                </p>
              </div>

              {/* COMMISSION */}
              {hasCommission && (
                <div className="flex items-center justify-between">
                  <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                    Comisión
                  </p>
                  <p className="text-sm text-gray-300">
                    {formatCurrency(commission, currency)}
                  </p>
                </div>
              )}

              {hasVat && (
                <div className="flex items-center justify-between">
                  <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                    IVA
                  </p>
                  <p className="text-sm text-gray-300">
                    {formatCurrency(vat, currency)}
                  </p>
                </div>
              )}

              {/* TOTAL */}
              <div className="border-t border-green-900/40 pt-3 flex items-center justify-between">
                <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                  Total
                </p>
                <p className="text-2xl font-bold text-green-400">
                  {formatCurrency(total, currency)}
                </p>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`w-full mt-6 py-4 rounded-xl font-bold tracking-widest uppercase text-sm transition
                ${
                  submitting
                    ? "bg-green-700 text-black/50 cursor-not-allowed"
                    : "bg-green-500 text-black hover:bg-green-400"
                }`}
            >
              {submitting ? "Procesando..." : "Ir al pago →"}
            </button>

            <p className="text-center text-[10px] text-gray-600 mt-4 tracking-wider uppercase">
              🔒 Pago seguro con encriptación SSL
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

/* ── HELPERS ── */

function SectionLabel({ label }) {
  return (
    <p className="text-[10px] tracking-widest text-green-400/80 uppercase border-b border-green-900/30 pb-2">
      {label}
    </p>
  );
}

function Field({
  label,
  value,
  onChange,
  error,
  placeholder,
  type = "text",
  disabled = false,
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] tracking-widest text-gray-400 uppercase">
        {label}
      </label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`bg-[#031c14] border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition
          ${
            error
              ? "border-red-500/60 focus:border-red-500"
              : "border-green-900/40 focus:border-green-500/60"
          }`}
      />
      {error && (
        <p className="text-[10px] text-red-400 tracking-wide">{error}</p>
      )}
    </div>
  );
}

function OrderItem({ item, onRemove, onQtyChange, formatCurrency }) {
  const remaining = getRemaining(item.ticket);
  const atMax = item.quantity >= remaining;
  const atMin = item.quantity <= 1;

  return (
    <div className="bg-[#031c14] border border-green-900/30 rounded-xl p-4">
      <p className="text-[10px] tracking-widest text-green-400/60 uppercase mb-1">
        {item.eventName}
      </p>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold">{item.ticket.name}</p>
        <button
          onClick={onRemove}
          className="text-gray-600 hover:text-red-400 transition flex-shrink-0 mt-0.5"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
      {item.ticket.tickets_per_order > 1 && (
        <p className="text-[10px] text-gray-500 mt-0.5">
          {item.ticket.tickets_per_order} entradas por unidad
        </p>
      )}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-1.5 bg-[#021610] rounded-full px-1 py-1 border border-green-900/40">
          <button
            onClick={() => onQtyChange(item.quantity - 1)}
            disabled={atMin}
            className={`w-6 h-6 rounded-full transition flex items-center justify-center text-sm leading-none
              ${atMin ? "text-green-900 cursor-not-allowed" : "text-green-400 hover:bg-green-900/50"}`}
          >
            −
          </button>
          <span className="text-sm w-5 text-center tabular-nums">
            {item.quantity}
          </span>
          <button
            onClick={() => onQtyChange(item.quantity + 1)}
            disabled={atMax}
            className={`w-6 h-6 rounded-full transition flex items-center justify-center text-sm leading-none
              ${atMax ? "text-green-900 cursor-not-allowed" : "text-green-400 hover:bg-green-900/50"}`}
          >
            +
          </button>
        </div>
        <p className="text-sm font-semibold text-white">
          {formatCurrency(item.ticket.price * item.quantity, item.currency)}
        </p>
      </div>
      {atMax && (
        <p className="text-[10px] text-yellow-500/70 mt-2 tracking-wide">
          Máximo disponible alcanzado
        </p>
      )}
    </div>
  );
}

function StepLine() {
  return <div className="flex-1 h-px bg-green-900/30 mx-3" />;
}

function StepDot({ number, label, active, done }) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div
        className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold
          ${done ? "bg-green-700 text-white" : ""}
          ${active ? "bg-green-500 text-black" : ""}
          ${!done && !active ? "border border-green-500/30 text-green-400" : ""}
        `}
      >
        {done ? "✓" : number}
      </div>
      <span className="hidden md:block whitespace-nowrap text-[10px] tracking-widest">
        {label}
      </span>
    </div>
  );
}
